import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/StatusBadge';
import { localeLabel } from '../utils/localeFlag';

const GID_PREFIXES = {
  COLLECTION: 'gid://shopify/Collection/',
  PAGE: 'gid://shopify/Page/',
  ARTICLE: 'gid://shopify/Article/',
  MENU: 'gid://shopify/Menu/',
};

// Phase 18 D-12: transforms the {rows: [...]} response from
// GET /api/transly/content-resources/by-resource-id/<gid>/ into the legacy
// `content` shape the rest of ResourceDetail.jsx expects.
//
// Legacy shape (from /api/transly/resource-content/ — see backend
// ResourceContentView in views.py:342): {
//   resourceId, fields: [{key, value, digest, type}],
//   translations: [{key, value, outdated?}], nestedResources: [...]
// }
//
// The new local-inventory endpoint returns one row per (resource_id,
// field_key) with per-locale status columns (de_status, es_status). The
// adapter recreates the legacy shape so the existing JSX consumer can
// render fields + per-locale translation values without a wider rewrite.
//
// For the translation `value`, we don't have the translated text on the new
// endpoint (it tracks per-locale STATUS only, not the text itself — the
// translation text lives in public.bulk_translations.translated_text, which
// ResourceContentView reads via a JOIN). So `translations[].value` is left
// empty for now; the per-field push path (D-13) still re-fetches digests
// fresh from Shopify before pushing — the existing "not translated" empty
// affordance handles the no-text case for the source-side read.
function adaptContentResourcesToLegacyShape(rows /* , locale (unused) */) {
  return {
    resourceId: rows[0]?.resource_id || null,
    fields: rows.map((r) => ({
      key: r.field_key,
      value: r.raw_value,
      digest: r.digest,
      type: r.content_type,
    })),
    // Local inventory doesn't carry translated_text; existing JSX shows
    // "not translated" italic when translation?.value is falsy. The D-13
    // push path still works because the per-field push handler re-fetches
    // the digest fresh from Shopify before pushing.
    translations: [],
    nestedResources: [],
  };
}

export default function ResourceDetail({ type }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const resourceId = `${GID_PREFIXES[type]}${id}`;

  const [content, setContent] = useState(null);
  const [locale, setLocale] = useState('es');
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});       // { "resourceId::key": "edited value" }
  const [pushing, setPushing] = useState(false);
  const [translatingMfIds, setTranslatingMfIds] = useState(new Set());

  useEffect(() => {
    axios.get('api/transly/locales/').then((res) => {
      const nonPrimary = res.data.filter((l) => !l.primary);
      setLocales(nonPrimary);
      if (nonPrimary.length > 0) setLocale(nonPrimary[0].locale);
    }).catch(() => {});
  }, []);

  const fetchContent = useCallback(async () => {
    if (!locale) return;
    setLoading(true);
    try {
      if (type === 'ARTICLE' || type === 'PAGE') {
        // Phase 18 D-12: read source-side from local inventory for
        // ARTICLE+PAGE only. COLLECTION (and any other type) keeps the
        // legacy live-fetch path below — explicit per Phase 15 D-05
        // regression invariant.
        const res = await axios.get(
          `api/transly/content-resources/by-resource-id/${encodeURIComponent(resourceId)}/`,
        );
        const rows = res.data?.rows || [];
        if (rows.length === 0) {
          // D-14: empty state — UI shows "Sync from Shopify" CTA below.
          setContent(null);
        } else {
          setContent(adaptContentResourcesToLegacyShape(rows, locale));
        }
      } else {
        // Legacy live-fetch path preserved for COLLECTION, MENU, etc.
        const res = await axios.get('api/transly/resource-content/', { params: { resourceId, locale } });
        setContent(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch content:', err);
    } finally {
      setLoading(false);
    }
  }, [resourceId, locale, type]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('api/transly/jobs/', { params: { id: jobId } });
        setJobStatus(res.data.status);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          setTranslating(false);
          setJobId(null);
          fetchContent();
        }
      } catch { clearInterval(interval); }
    }, 2000);
    return () => clearInterval(interval);
  }, [jobId, fetchContent]);

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post('api/transly/translate/', { resourceIds: [resourceId], resourceType: type, targetLocale: locale });
      setJobId(res.data.jobId);
      setJobStatus('in_progress');
    } catch (err) {
      alert('Translation failed: ' + (err.response?.data?.error || err.message));
      setTranslating(false);
    }
  };

  // D-06: per-metafield "Translate now" — scoped to active `locale` (no multi-locale fan-out).
  // Wraps Plan 16-01b endpoint: POST /api/transly/metafields/<id>/translate-one/
  const handleTranslateOneMetafield = async (metafieldId) => {
    setTranslatingMfIds((prev) => new Set([...prev, metafieldId]));
    try {
      await axios.post(`api/transly/metafields/${metafieldId}/translate-one/`, {
        target_locale: locale,
      });
      await fetchContent();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'unknown error';
      alert(`Translate now failed: ${msg}`);
    } finally {
      setTranslatingMfIds((prev) => {
        const next = new Set(prev);
        next.delete(metafieldId);
        return next;
      });
    }
  };

  const setEdit = (resId, key, value) => {
    setEdits(prev => ({ ...prev, [`${resId}::${key}`]: value }));
  };

  const getEdit = (resId, key) => edits[`${resId}::${key}`];
  const hasEdits = Object.keys(edits).length > 0;

  const handleCancelEdit = () => {
    setEditing(false);
    setEdits({});
  };

  const handlePushEdits = async () => {
    setPushing(true);
    try {
      // Group edits by resourceId
      const byResource = {};
      for (const [compositeKey, value] of Object.entries(edits)) {
        const separatorIdx = compositeKey.indexOf('::');
        const editResId = compositeKey.substring(0, separatorIdx);
        const fieldKey = compositeKey.substring(separatorIdx + 2);
        if (!byResource[editResId]) byResource[editResId] = [];
        // Find the digest for this field
        let digest = null;
        let sourceValue = null;
        if (editResId === resourceId) {
          const field = content.fields?.find(f => f.key === fieldKey);
          digest = field?.digest;
          sourceValue = field?.value;
        } else {
          const nested = content.nestedResources?.find(n => n.resourceId === editResId);
          const field = nested?.translatableContent?.find(f => f.key === fieldKey);
          digest = field?.digest;
          sourceValue = field?.value;
        }
        if (digest) {
          byResource[editResId].push({ key: fieldKey, value, digest, sourceValue });
        }
      }

      // Push each resource's translations
      const errors = [];
      for (const [resId, translations] of Object.entries(byResource)) {
        try {
          await axios.post('api/transly/push-translation/', { resourceId: resId, locale, translations });
        } catch (err) {
          errors.push(`${resId}: ${err.response?.data?.error || err.message}`);
        }
      }

      if (errors.length > 0) {
        alert('Some translations failed:\n' + errors.join('\n'));
      }

      setEditing(false);
      setEdits({});
      fetchContent();
    } catch (err) {
      alert('Push failed: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  const getTranslation = (key) => content?.translations?.find((t) => t.key === key);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <h1 className="text-2xl font-bold text-foreground mb-1">{type} Detail</h1>
      <p className="text-sm text-muted-foreground mb-6">{resourceId}</p>

      <div className="flex gap-2 mb-6">
        {locales.map((l) => (
          <Button key={l.locale} variant={locale === l.locale ? 'default' : 'outline'} size="sm" onClick={() => setLocale(l.locale)}>
            {localeLabel(l.locale)}
          </Button>
        ))}
        {!editing ? (
          <>
            <Button variant="outline" className="ml-auto" onClick={() => setEditing(true)} disabled={translating || loading}>
              Edit Translations
            </Button>
            <Button onClick={handleTranslate} disabled={translating}>
              {translating ? `Translating... (${jobStatus})` : `Translate to ${localeLabel(locale)}`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" className="ml-auto" onClick={handleCancelEdit} disabled={pushing}>
              Cancel
            </Button>
            <Button onClick={handlePushEdits} disabled={pushing || !hasEdits}>
              {pushing ? 'Pushing...' : `Push ${Object.keys(edits).length} change${Object.keys(edits).length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading content...</div>
      ) : content === null && (type === 'ARTICLE' || type === 'PAGE') ? (
        // D-14: empty-state UX when transly.content_resources has no row
        // for this resource (sync hasn't run yet, or the resource was
        // deleted upstream). Mirrors Phase 9 D-21 / Phase 16 D-08.
        <div className="border rounded p-8 text-center">
          <p className="text-muted-foreground mb-4">
            No translations stored locally for this {type === 'ARTICLE' ? 'article' : 'page'}.
          </p>
          <Button
            onClick={() => navigate(type === 'ARTICLE' ? '/articles' : '/pages-content')}
            variant="outline"
          >
            Go to inventory and Sync from Shopify
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {(content?.fields || []).filter((f) => f.value).map((field) => {
            const translation = getTranslation(field.key);
            return (
              <Card key={field.key}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">{field.key}</CardTitle>
                    {translation?.outdated && <StatusBadge status="outdated" />}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">English</p>
                      <div className="text-sm bg-muted p-2 rounded-md min-h-[40px] break-words text-foreground">{field.value}</div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{localeLabel(locale)}</p>
                      {editing ? (
                        field.key === 'body_html' || field.key === 'summary_html' ? (
                          <textarea
                            className="w-full text-sm bg-background border border-input p-2 rounded-md min-h-[80px] break-words text-foreground resize-y"
                            value={getEdit(resourceId, field.key) ?? translation?.value ?? ''}
                            onChange={(e) => setEdit(resourceId, field.key, e.target.value)}
                            rows={4}
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full text-sm bg-background border border-input p-2 rounded-md min-h-[40px] text-foreground"
                            value={getEdit(resourceId, field.key) ?? translation?.value ?? ''}
                            onChange={(e) => setEdit(resourceId, field.key, e.target.value)}
                          />
                        )
                      ) : (
                        <div className="text-sm bg-primary/5 p-2 rounded-md min-h-[40px] break-words text-foreground">
                          {translation?.value || <span className="text-muted-foreground italic">not translated</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* D-06: read-only Metafields card + per-row Translate-now. Gated to COLLECTION type. */}
          {type === 'COLLECTION' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Metafields</CardTitle>
              </CardHeader>
              <CardContent>
                {(content?.metafields || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No metafields synced for this collection yet.{' '}
                    <Button
                      variant="link"
                      size="sm"
                      className="px-0 h-auto"
                      onClick={() => navigate('/metafields/collection')}
                    >
                      Sync from Shopify
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(content?.metafields || []).map((mf) => {
                      const isTranslating = translatingMfIds.has(mf.id);
                      return (
                        <div key={mf.id}>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-mono text-xs text-muted-foreground">
                              {mf.namespace}.{mf.key}
                            </p>
                            {mf.translation?.outdated && <StatusBadge status="outdated" />}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">English</p>
                              <div className="text-sm bg-muted p-2 rounded-md min-h-[40px] break-words text-foreground">
                                {mf.value}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">{localeLabel(locale)}</p>
                              <div className="text-sm bg-primary/5 p-2 rounded-md min-h-[40px] break-words text-foreground">
                                {mf.translation?.value || (
                                  <span className="text-muted-foreground italic">not translated</span>
                                )}
                                <div className="mt-2 flex justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleTranslateOneMetafield(mf.id)}
                                    disabled={isTranslating || !mf.value}
                                  >
                                    {isTranslating ? 'Translating…' : 'Translate now'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
